// @ts-nocheck
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import LogoToolBar from "@/assets/svgComponets/LogoToolbar.tsx";
import { Lock, LockKeyhole } from "lucide-react";
import React, { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import Divider from "@/components/others/Separator";
import Input from "@/components/form/Input/Input.tsx";
import Button from "@/components/Buttons/Button";
import { ResetPasswordScheme } from "@/integration/user/interfaces/validations/auth.schema.ts";
import {
  IChangePassword,
  IChangePasswordParams,
} from "@/integration/user/interfaces/auth.interface.ts";
import useAuth from "@integration/user/hooks/useAuth.ts";

// Glass UI animations
const gameUIAnimations = `
@keyframes pulse {
  0% { opacity: 0.3; }
  50% { opacity: 0.7; }
  100% { opacity: 0.3; }
}

@keyframes subtleFloat {
  0% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0); }
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  100% { background-position: 100% 50%; }
}

@keyframes borderPulse {
  0% { border-color: rgba(255, 255, 255, 0.1); }
  50% { border-color: rgba(255, 255, 255, 0.3); }
  100% { border-color: rgba(255, 255, 255, 0.1); }
}

@keyframes buttonHover {
  0% { background-color: rgba(255, 255, 255, 0.1); }
  100% { background-color: rgba(255, 255, 255, 0.2); }
}

@keyframes cardPulse {
  0% { transform: scale(1); box-shadow: 0 0 20px rgba(255, 255, 255, 0.1); }
  50% { transform: scale(1.005); box-shadow: 0 0 30px rgba(255, 255, 255, 0.15); }
  100% { transform: scale(1); box-shadow: 0 0 20px rgba(255, 255, 255, 0.1); }
}`;

export default function NewPassword() {
  // Apply glass UI animations
  useEffect(() => {
    // Add style tag with animations
    const styleTag = document.createElement("style");
    styleTag.innerHTML = gameUIAnimations;
    document.head.appendChild(styleTag);

    // Clean up on unmount
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  // Background images for the recovery page
  const backgroundImages = [
    "https://pbs.twimg.com/media/GT_VN9BXQAA28su.jpg",
    "https://image.api.playstation.com/vulcan/ap/rnd/202407/1814/91f55ab9e79e1853fa1094bcd9cbe88ea6ddba474675c75d.jpg",
    "https://static1.cbrimages.com/wordpress/wp-content/uploads/2024/12/reports-show-arcane-didn-t-lead-to-new-lol-players-riot-responds.jpg",
    "https://i.pinimg.com/originals/ff/c3/8d/ffc38d9bce22d7a9eaa07b890e3af1a2.jpg",
  ];

  const [currentBgIndex, setCurrentBgIndex] = useState(0);

  // Change background image every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBgIndex(
        (prevIndex) => (prevIndex + 1) % backgroundImages.length
      );
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  const { submiteChangePassword, isAuthenticated } = useAuth();
  const navegate = useNavigate();
  const location = useLocation();

  const [queryData, setQueryData] = useState<IChangePasswordParams>({
    token: "",
    email: "",
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get("token") || "";
    const email = urlParams.get("email") || "";
    setQueryData({ token, email });
  }, [location.search]);

  if (isAuthenticated) {
    navegate("/");
  }

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitted },
  } = useForm<IChangePassword>({
    resolver: yupResolver(ResetPasswordScheme),
  });

  const handleCancel = () => {
    navegate("/auth");
  };

  return (
    <div className="relative w-screen min-h-screen overflow-y-auto">
      {/* Dynamic background with parallax effect */}
      {backgroundImages.map((img, index) => (
        <div
          key={index}
          className={`fixed inset-0 w-full h-full transition-opacity duration-1500 ease-in-out ${
            index === currentBgIndex ? "opacity-100" : "opacity-0"
          }`}
          style={{
            backgroundImage: `url(${img})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "brightness(0.4)",
            transform: `scale(${index === currentBgIndex ? 1.05 : 1})`,
            transition: "transform 8s ease-out, opacity 1.5s ease-in-out",
          }}
        />
      ))}

      {/* Subtle grid overlay pattern */}
      <div
        className="fixed inset-0 w-full h-full"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0, 0, 0, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 0, 0, 0.3) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
          backgroundPosition: "center center",
        }}
      />

      {/* Subtle background gradients */}
      <div className="fixed top-0 left-0 w-full h-20 bg-gradient-to-b from-white/5 to-transparent"></div>
      <div className="fixed bottom-0 left-0 w-full h-20 bg-gradient-to-t from-white/5 to-transparent"></div>
      <div className="fixed top-0 left-0 h-full w-20 bg-gradient-to-r from-white/5 to-transparent"></div>
      <div className="fixed top-0 right-0 h-full w-20 bg-gradient-to-l from-white/5 to-transparent"></div>

      {/* Content container */}
      <div className="relative z-10 w-full min-h-screen flex items-center justify-center py-8 px-4">
        <motion.div
          className="w-full max-w-md"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 100, damping: 15 }}
        >
          {/* Glass-style form with rounded borders */}
          <motion.form
            id="form-new-recovery"
            onSubmit={handleSubmit(
              async (
                data: IChangePassword,
                event?: React.BaseSyntheticEvent
              ) => {
                await submiteChangePassword({ ...data, ...queryData }, event);
                reset();
                navegate("/auth");
              }
            )}
            className="relative flex flex-col w-full p-8 bg-gray-900/70 backdrop-blur-xl border border-white/20 shadow-xl overflow-hidden rounded-xl"
            style={{
              boxShadow:
                "0 0 30px rgba(255, 255, 255, 0.1), inset 0 0 15px rgba(255, 255, 255, 0.05)",
              animation: "cardPulse 8s infinite ease-in-out",
              transform: "perspective(1000px) rotateX(1deg)",
            }}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 80, damping: 10 }}
          >
            {/* Simple header with glass effect */}
            <div className="relative flex flex-col gap-4 items-center justify-center mb-8 z-10">
              {/* Logo */}
              <div className="relative">
                <LogoToolBar />
              </div>

              {/* Title */}
              <div className="relative">
                <h1 className="text-white font-saira font-black text-2xl md:text-3xl tracking-wider">
                  NOVA SENHA
                </h1>
                <div className="absolute -bottom-1 left-0 right-0 h-px bg-white/20"></div>
              </div>

              {/* Simple divider */}
              <div className="w-32 h-px mt-2 bg-white/20"></div>

              <p className="text-white/80 text-center text-sm md:text-base">
                Crie uma nova senha para sua conta.
              </p>
            </div>

            {/* Form fields with glass styling */}
            <div className="mb-4">
              <Input
                id="inp-password"
                iconLeft={<Lock size={18} className="text-white/70" />}
                {...register("password")}
                label="Senha"
                type="password"
                placeholder="Senha"
                className="bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-md focus:border-white/40 focus:ring-white/20 placeholder:text-white/50 transition-all duration-300"
              />
              {isSubmitted &&
                errors.password &&
                errors.password
                  .message!.split("\n")
                  .map((item: string, index: number) => (
                    <span
                      className="text-sm mt-1 text-red-400 font-medium"
                      key={index}
                    >
                      {item}
                    </span>
                  ))}
            </div>

            <div className="mb-6">
              <Input
                id="inp-confirm-password"
                iconLeft={<LockKeyhole size={18} className="text-white/70" />}
                {...register("passwordConfirm")}
                label="Confirmar Senha"
                type="password"
                placeholder="Confirmar Senha"
                className="bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-md focus:border-white/40 focus:ring-white/20 placeholder:text-white/50 transition-all duration-300"
              />
              {isSubmitted && errors.passwordConfirm && (
                <span className="text-sm mt-1 text-red-400 font-medium">
                  {errors.passwordConfirm.message}
                </span>
              )}
            </div>

            <div className="flex flex-col md:flex-row gap-4 mt-4">
              <Button
                id="btn-recovery"
                className="w-full py-3 transition-all duration-300 transform hover:scale-105 relative z-10"
                backgroundColor="bg-primary-600"
                hoverColor="hover:bg-primary-700"
                label={
                  <span className="tracking-wider font-bold">CONFIRMAR</span>
                }
                type="submit"
              />
              <Button
                id="btn-auth-redirect"
                outline
                type="button"
                textColor="text-primary-500"
                borderColor="border-primary-500"
                ringColor="focus:ring-primary-400"
                className="w-full py-3   transition-all duration-300 transform hover:scale-105 relative z-10"
                label={<span className="tracking-wider">VOLTAR AO LOGIN</span>}
                onClick={handleCancel}
              />
            </div>

            <div className="mt-6">
              <Divider />
            </div>
          </motion.form>
        </motion.div>
      </div>
    </div>
  );
}